import {
  ComponentType,
  createContext,
  forwardRef,
  isValidElement,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size as floatingSize,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
  useHover,
  useInteractions,
  useListItem,
  useListNavigation,
  useMergeRefs,
  useRole,
  useTypeahead,
  safePolygon,
  FloatingFocusManager,
  FloatingList,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  Placement,
} from "@floating-ui/react";
import { Flex, Icon, Input, Checkbox } from "@repo/ping-design";
import type { IconName } from "../Icon";
import { Chip } from "../Chip/Chip";
import cx from "classnames";
import "./MultiDropdown.scss";

type MultiDropdownItem = {
  id: string;
  label: string | ReactNode;
  icon?: string | ReactNode;
  onClick?: () => void;
  subItems?: MultiDropdownItem[];
  /**
   * Controls whether the dropdown closes after this item's `onClick` fires.
   * Defaults to `"CLOSE"`.
   */
  behavior?: "KEEP_OPEN" | "CLOSE";
  /**
   * If set, a checkbox is rendered before the label. Combined with
   * `indeterminate`, this lets parent items show aggregate child selection.
   */
  checked?: boolean;
  indeterminate?: boolean;
  isActive?: boolean;
  disabled?: boolean;
};

type MultiDropdownTriggerVariant = "dropdown" | "combobox";

type MultiDropdownSelectedItem = {
  value: string;
  label: ReactNode;
};

type MultiDropdownProps = {
  items: MultiDropdownItem[];
  onOpenChange?: (open: boolean) => void;
  className?: string;
  onSearchChange?: (value: string) => void;

  trigger?: ReactNode;

  triggerVariant?: MultiDropdownTriggerVariant;
  triggerIcon?: IconName | ReactElement | ComponentType<any>;
  triggerIconProps?: Record<string, any>;
  placeholder?: string;
  triggerSize?: "md" | "lg";
  selectedItems?: MultiDropdownSelectedItem[];
  onRemoveSelectedItem?: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;

  placement?: Placement;
  submenuPlacement?: SubmenuPlacement;
  offsetX?: number;
  offsetY?: number;
  dropdownWidth?: React.CSSProperties["width"];
  usePortal?: boolean;
  matchTriggerWidth?: boolean;

  triggerClasses?: string;
  triggerLabelClasses?: string;
  menuClasses?: string;
  menuItemClasses?: string;
};

type MenuContextValue = {
  getItemProps: (
    userProps?: React.HTMLProps<HTMLElement>,
  ) => Record<string, unknown>;
  activeIndex: number | null;
  setActiveIndex: (i: number | null) => void;
  setHasFocusInside: (b: boolean) => void;
  isOpen: boolean;
};

const MenuContext = createContext<MenuContextValue>({
  getItemProps: () => ({}),
  activeIndex: null,
  setActiveIndex: () => {},
  setHasFocusInside: () => {},
  isOpen: false,
});

type SubmenuPlacement = "left" | "right";

type ItemRowContentProps = {
  item: MultiDropdownItem;
  hasSubmenu: boolean;
  submenuPlacement?: SubmenuPlacement;
};

const ItemRowContent = ({
  item,
  hasSubmenu,
  submenuPlacement = "right",
}: ItemRowContentProps) => {
  const showCheckbox =
    item.checked !== undefined || item.indeterminate !== undefined;
  const opensLeft = submenuPlacement === "left";
  return (
    <>
      {hasSubmenu && opensLeft && <Icon iconName="ChevronLeft" size={14} />}
      <Flex
        gap="1"
        align="center"
        direction={opensLeft ? "row-reverse" : "row"}
        className="MultiDropdown__FloatingMenu__Item__Label"
      >
        {showCheckbox && (
          <Checkbox
            as="component"
            checked={!!item.checked}
            indeterminate={!!item.indeterminate}
            disabled={item.disabled}
          />
        )}
        {item.icon && (
          <Flex align="center">
            {typeof item.icon === "string" ? (
              <Icon iconName={item.icon as IconName} size={14} />
            ) : (
              item.icon
            )}
          </Flex>
        )}
        <span>{item.label}</span>
      </Flex>
      {hasSubmenu && !opensLeft && <Icon iconName="ChevronRight" size={14} />}
    </>
  );
};

const itemLabelText = (item: MultiDropdownItem) =>
  item.disabled
    ? null
    : typeof item.label === "string"
      ? item.label
      : item.id;

const stopMenuSearchKeyDown = (e: React.KeyboardEvent) => {
  if (e.key !== "Escape") e.stopPropagation();
};

const MultiDropdownLeaf = forwardRef<
  HTMLDivElement,
  {
    item: MultiDropdownItem;
    menuItemClasses?: string;
    submenuPlacement?: SubmenuPlacement;
  }
>(({ item, menuItemClasses, submenuPlacement = "right" }, forwardedRef) => {
  const menu = useContext(MenuContext);
  const tree = useFloatingTree();
  const listItem = useListItem({ label: itemLabelText(item) });
  const isFocused = listItem.index === menu.activeIndex;
  const ref = useMergeRefs([listItem.ref, forwardedRef]);

  return (
    <div
      ref={ref}
      role="menuitem"
      tabIndex={isFocused ? 0 : -1}
      className={cx(
        "MultiDropdown__FloatingMenu__Item",
        {
          "MultiDropdown__FloatingMenu__Item--Active": item.isActive,
          "MultiDropdown__FloatingMenu__Item--Disabled": item.disabled,
          "MultiDropdown__FloatingMenu__Item--Focused": isFocused,
        },
        menuItemClasses,
      )}
      {...menu.getItemProps({
        onClick() {
          if (item.disabled) return;
          item.onClick?.();
          if (!item.behavior || item.behavior === "CLOSE") {
            tree?.events.emit("click");
          }
        },
        onKeyDown(e: React.KeyboardEvent) {
          if (item.disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            item.onClick?.();
            if (!item.behavior || item.behavior === "CLOSE") {
              tree?.events.emit("click");
            }
          }
        },
        onFocus() {
          menu.setHasFocusInside(true);
        },
      })}
    >
      <ItemRowContent
        item={item}
        hasSubmenu={false}
        submenuPlacement={submenuPlacement}
      />
    </div>
  );
});
MultiDropdownLeaf.displayName = "MultiDropdownLeaf";

const MultiDropdownSubmenu = forwardRef<
  HTMLDivElement,
  {
    item: MultiDropdownItem;
    menuClasses?: string;
    menuItemClasses?: string;
    submenuPlacement?: SubmenuPlacement;
  }
>(({ item, menuClasses, menuItemClasses, submenuPlacement = "right" }, forwardedRef) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [hasFocusInside, setHasFocusInside] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isOverflowing, setIsOverflowing] = useState(false);
  const itemsContainerRef = useRef<HTMLDivElement>(null);

  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  // When the search box is visible, typing should land in the input rather
  // than driving the list's typeahead navigation.
  const showSearch = isOverflowing || searchQuery.length > 0;

  const tree = useFloatingTree();
  const nodeId = useFloatingNodeId();
  const listItem = useListItem({ label: itemLabelText(item) });
  const parent = useContext(MenuContext);

  const { context, refs, floatingStyles } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: submenuPlacement === "left" ? "left-start" : "right-start",
    middleware: [offset({ mainAxis: 4, crossAxis: -4 }), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    enabled: !item.disabled,
    mouseOnly: true,
    delay: { open: 75, close: 150 },
    handleClose: safePolygon({ blockPointerEvents: true }),
  });
  const click = useClick(context, { enabled: !item.disabled, ignoreMouse: true });
  const role = useRole(context, { role: "menu" });
  const dismiss = useDismiss(context, { bubbles: true });
  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    nested: true,
    onNavigate: setActiveIndex,
  });
  const typeahead = useTypeahead(context, {
    enabled: !showSearch,
    listRef: labelsRef,
    onMatch: isOpen ? setActiveIndex : undefined,
    activeIndex,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [hover, click, role, dismiss, listNavigation, typeahead],
  );

  useEffect(() => {
    if (!tree) return;
    const handleTreeClick = () => setIsOpen(false);
    tree.events.on("click", handleTreeClick);
    return () => tree.events.off("click", handleTreeClick);
  }, [tree]);

  const filteredSubItems = (item.subItems || []).filter((subItem) => {
    if (!searchQuery) return true;
    const labelText = typeof subItem.label === "string" ? subItem.label : "";
    return labelText.toLowerCase().includes(searchQuery.toLowerCase());
  });

  useEffect(() => {
    if (isOpen) {
      const rafId = requestAnimationFrame(() => {
        if (itemsContainerRef.current) {
          const el = itemsContainerRef.current;
          setIsOverflowing(el.scrollHeight > el.clientHeight);
        }
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [isOpen, item.subItems]);

  const isFocused = listItem.index === parent.activeIndex;
  const referenceRef = useMergeRefs([
    refs.setReference,
    listItem.ref,
    forwardedRef,
  ]);

  return (
    <FloatingNode id={nodeId}>
      <div
        ref={referenceRef}
        role="menuitem"
        tabIndex={isFocused ? 0 : -1}
        className={cx(
          "MultiDropdown__FloatingMenu__Item",
          {
            "MultiDropdown__FloatingMenu__Item--Active": item.isActive,
            "MultiDropdown__FloatingMenu__Item--Disabled": item.disabled,
            "MultiDropdown__FloatingMenu__Item--Focused": isFocused,
            "MultiDropdown__FloatingMenu__Item--HasOnClick": !!item.onClick,
          },
          menuItemClasses,
        )}
        {...getReferenceProps(
          parent.getItemProps({
            onClick() {
              if (item.disabled || !item.onClick) return;
              item.onClick();
              if (!item.behavior || item.behavior === "CLOSE") {
                tree?.events.emit("click");
              }
            },
            onKeyDown(e: React.KeyboardEvent) {
              if (item.disabled || !item.onClick) return;
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                item.onClick();
                if (!item.behavior || item.behavior === "CLOSE") {
                  tree?.events.emit("click");
                }
              }
            },
            onFocus() {
              parent.setHasFocusInside(true);
              setHasFocusInside(false);
            },
          }),
        )}
      >
        <ItemRowContent
          item={item}
          hasSubmenu
          submenuPlacement={submenuPlacement}
        />
      </div>

      <MenuContext.Provider
        value={{
          activeIndex,
          setActiveIndex,
          getItemProps,
          setHasFocusInside,
          isOpen,
        }}
      >
        <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
          {isOpen && (
            <FloatingPortal>
              <FloatingFocusManager
                context={context}
                modal={false}
                initialFocus={hasFocusInside ? -1 : 0}
                returnFocus={false}
              >
                <div
                  ref={refs.setFloating}
                  style={floatingStyles}
                  className={cx(
                    "MultiDropdown__FloatingMenu",
                    {
                      "MultiDropdown__FloatingMenu--SubmenuLeft":
                        submenuPlacement === "left",
                    },
                    menuClasses,
                  )}
                  {...getFloatingProps()}
                >
                  {showSearch && (
                    <div
                      className="MultiDropdown__FloatingMenu__Search"
                      onKeyDown={stopMenuSearchKeyDown}
                    >
                      <Input
                        name="submenu-search"
                        placeholder="Filter"
                        value={searchQuery}
                        onChange={(_, value) => setSearchQuery(value)}
                        suffix={<Icon iconName="Search" size={14} />}
                      />
                    </div>
                  )}
                  <div
                    ref={itemsContainerRef}
                    className="MultiDropdown__FloatingMenu__Items"
                  >
                    {filteredSubItems.length === 0 ? (
                      <div className="MultiDropdown__FloatingMenu__NoItems">
                        No items found
                      </div>
                    ) : (
                      filteredSubItems.map((subItem) =>
                        subItem.subItems && subItem.subItems.length > 0 ? (
                          <MultiDropdownSubmenu
                            key={subItem.id}
                            item={subItem}
                            menuClasses={menuClasses}
                            menuItemClasses={menuItemClasses}
                            submenuPlacement={submenuPlacement}
                          />
                        ) : (
                          <MultiDropdownLeaf
                            key={subItem.id}
                            item={subItem}
                            menuItemClasses={menuItemClasses}
                            submenuPlacement={submenuPlacement}
                          />
                        ),
                      )
                    )}
                  </div>
                </div>
              </FloatingFocusManager>
            </FloatingPortal>
          )}
        </FloatingList>
      </MenuContext.Provider>
    </FloatingNode>
  );
});
MultiDropdownSubmenu.displayName = "MultiDropdownSubmenu";

const MultiDropdownInternal: React.FC<MultiDropdownProps> = ({
  items,
  onOpenChange,
  className,
  onSearchChange,

  trigger,
  triggerVariant = "dropdown",
  triggerIcon,
  triggerIconProps = {},
  placeholder,
  triggerSize = "md",
  selectedItems,
  onRemoveSelectedItem,
  label,
  required = false,
  disabled = false,
  error = false,

  placement = "bottom-start",
  submenuPlacement = "right",
  offsetX = 0,
  offsetY = 4,
  dropdownWidth,
  usePortal = true,
  matchTriggerWidth = false,

  triggerClasses,
  triggerLabelClasses,
  menuClasses,
  menuItemClasses,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [hasFocusInside, setHasFocusInside] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  const tree = useFloatingTree();
  const nodeId = useFloatingNodeId();

  const { context, refs, floatingStyles } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange: (open) => {
      if (disabled) return;
      setIsOpen(open);
      onOpenChange?.(open);
    },
    placement,
    middleware: [
      offset({ mainAxis: offsetY, crossAxis: offsetX }),
      flip(),
      shift(),
      floatingSize({
        apply({ rects, elements }) {
          if (matchTriggerWidth || dropdownWidth) {
            Object.assign(elements.floating.style, {
              width: dropdownWidth
                ? typeof dropdownWidth === "number"
                  ? `${dropdownWidth}px`
                  : dropdownWidth
                : `${rects.reference.width}px`,
            });
          }
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context, { enabled: !disabled, event: "mousedown" });
  const dismiss = useDismiss(context, { bubbles: false });
  const role = useRole(context, { role: "menu" });
  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
  });
  const typeahead = useTypeahead(context, {
    enabled: !onSearchChange,
    listRef: labelsRef,
    onMatch: isOpen ? setActiveIndex : undefined,
    activeIndex,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, dismiss, role, listNavigation, typeahead],
  );

  useEffect(() => {
    if (!tree) return;
    const handleTreeClick = () => setIsOpen(false);
    tree.events.on("click", handleTreeClick);
    return () => tree.events.off("click", handleTreeClick);
  }, [tree]);

  const handleSearchChange = (_: string, value: string) => {
    setSearchQuery(value);
    onSearchChange?.(value);
  };

  const renderTriggerIcon = () => {
    if (!triggerIcon) return null;
    if (typeof triggerIcon === "string") {
      return (
        <Icon iconName={triggerIcon as IconName} {...triggerIconProps} />
      );
    }
    if (isValidElement(triggerIcon)) return triggerIcon;
    const IconComponent = triggerIcon as ComponentType<any>;
    return <IconComponent {...triggerIconProps} />;
  };

  const hasSelection = (selectedItems?.length ?? 0) > 0;

  const renderBuiltInTrigger = () => {
    const sharedClasses = cx(
      "MultiDropdown__Trigger",
      `MultiDropdown__Trigger--${triggerVariant === "combobox" ? "Combobox" : "Dropdown"}`,
      {
        "MultiDropdown__Trigger--IsOpen": isOpen,
        "MultiDropdown__Trigger--Error": error,
        "MultiDropdown__Trigger--Disabled": disabled,
        "MultiDropdown__Trigger--Large": triggerSize === "lg",
      },
      triggerClasses,
    );

    const labelClasses = cx(
      "MultiDropdown__Trigger__Label",
      { "MultiDropdown__Trigger__Label--Filled": hasSelection },
      triggerLabelClasses,
    );

    const renderLabelArea = () => {
      if (triggerVariant === "combobox") {
        if (!hasSelection) {
          return (
            <div className={labelClasses}>
              {placeholder ?? "No selection"}
            </div>
          );
        }
        return (
          <div
            className="MultiDropdown__Trigger__Chips"
            onClick={(e) => {
              if (onRemoveSelectedItem) e.stopPropagation();
            }}
          >
            {selectedItems!.map((entry) => (
              <Chip
                key={entry.value}
                label={
                  typeof entry.label === "string"
                    ? entry.label
                    : (entry.value as string)
                }
                onClick={
                  onRemoveSelectedItem
                    ? () => onRemoveSelectedItem(entry.value)
                    : undefined
                }
              />
            ))}
          </div>
        );
      }

      const dropdownLabel = hasSelection
        ? selectedItems![0].label
        : (placeholder ?? "No selection");
      return <div className={labelClasses}>{dropdownLabel}</div>;
    };

    return (
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={sharedClasses}
        tabIndex={disabled ? -1 : 0}
      >
        <Flex direction="row" align="center" gap="1">
          {triggerIcon && (
            <div className="MultiDropdown__Trigger__Prefix">
              {renderTriggerIcon()}
            </div>
          )}
          {renderLabelArea()}
        </Flex>
        <div className="MultiDropdown__Trigger__Suffix">
          <Icon iconName={isOpen ? "ChevronUp" : "ChevronDown"} size={14} />
        </div>
      </div>
    );
  };

  const renderMenuContents = () => (
    <>
      {onSearchChange && (
        <div
          className="MultiDropdown__FloatingMenu__Search"
          onKeyDown={stopMenuSearchKeyDown}
        >
          <Input
            name="main-search"
            placeholder="Filter"
            value={searchQuery}
            onChange={handleSearchChange}
            suffix={<Icon iconName="Search" size={14} />}
          />
        </div>
      )}
      <div className="MultiDropdown__FloatingMenu__Items">
        {items.length === 0 ? (
          <div className="MultiDropdown__FloatingMenu__NoItems">
            No items found
          </div>
        ) : (
          items.map((item) =>
            item.subItems && item.subItems.length > 0 ? (
              <MultiDropdownSubmenu
                key={item.id}
                item={item}
                menuClasses={menuClasses}
                menuItemClasses={menuItemClasses}
                submenuPlacement={submenuPlacement}
              />
            ) : (
              <MultiDropdownLeaf
                key={item.id}
                item={item}
                menuItemClasses={menuItemClasses}
                submenuPlacement={submenuPlacement}
              />
            ),
          )
        )}
      </div>
    </>
  );

  const renderMenu = () => (
    <div
      ref={refs.setFloating}
      className={cx(
        "MultiDropdown__FloatingMenu",
        {
          "MultiDropdown__FloatingMenu--SubmenuLeft": submenuPlacement === "left",
        },
        menuClasses,
      )}
      style={floatingStyles}
      {...getFloatingProps()}
    >
      {renderMenuContents()}
    </div>
  );

  return (
    <FloatingNode id={nodeId}>
      <div
        className={cx(
          "MultiDropdown",
          { "MultiDropdown--Disabled": disabled },
          className,
        )}
      >
        {label && (
          <div
            className={cx("MultiDropdown__Label", {
              "MultiDropdown__Label--Error": error,
            })}
          >
            {label}
            {required && <span className="MultiDropdown__Required">*</span>}
          </div>
        )}

        {trigger ? (
          <div
            ref={refs.setReference}
            className="MultiDropdown__TriggerWrapper"
            {...getReferenceProps()}
          >
            {trigger}
          </div>
        ) : (
          renderBuiltInTrigger()
        )}

        <MenuContext.Provider
          value={{
            activeIndex,
            setActiveIndex,
            getItemProps,
            setHasFocusInside,
            isOpen,
          }}
        >
          <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
            {isOpen &&
              !disabled &&
              (usePortal ? (
                <FloatingPortal>
                  <FloatingFocusManager
                    context={context}
                    modal={false}
                    initialFocus={hasFocusInside ? -1 : 0}
                  >
                    {renderMenu()}
                  </FloatingFocusManager>
                </FloatingPortal>
              ) : (
                renderMenu()
              ))}
          </FloatingList>
        </MenuContext.Provider>
      </div>
    </FloatingNode>
  );
};

const MultiDropdown: React.FC<MultiDropdownProps> = (props) => {
  const parentId = useFloatingParentNodeId();
  if (parentId === null) {
    return (
      <FloatingTree>
        <MultiDropdownInternal {...props} />
      </FloatingTree>
    );
  }
  return <MultiDropdownInternal {...props} />;
};

export {
  MultiDropdown,
  type MultiDropdownItem,
  type MultiDropdownProps,
  type MultiDropdownSelectedItem,
  type MultiDropdownTriggerVariant,
};
